// Booking someone in who is standing in front of her.
//
// Most of Viktoria's work arrives in person or over the phone, and until now she
// recorded it by typing into Google Calendar — which is why the calendar and the
// database disagree about her diary. Moving that onto this system only works if
// entering a booking here is faster than typing one into a calendar app, and if
// it never demands something the client has not given her.
//
// So: a name is the only required field. No email, no phone, no consent
// checkbox. Everything else lives behind a disclosure that the main path never
// touches. `bookings.email` is nullable and `create_booking` requires an address
// only for online_self bookings (20260824020000).
//
// Slots come from getAdminSlots, which routes through the availability edge
// function — so her own Google Calendar is still honoured and she cannot be
// offered a time she has blocked out. That matters more here than anywhere else:
// she is booking at speed with somebody waiting, and a double-booking made in
// person is one she will turn up to.
//
// Deliberately no free-text time field. create_booking re-checks the slot in
// Postgres only, with no Google leg, so any path that supplies a time without
// going through getAdminSlots bypasses the calendar check entirely.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import supabaseService from '../services/supabaseService';

const DAYS_PER_PAGE = 7;

const toISODate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? 'pm' : 'am';
  return `${hour % 12 || 12}:${m}${period}`;
};

const formatDayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const today = toISODate(new Date());
  if (iso === today) return 'Today';
  const tomorrow = toISODate(new Date(Date.now() + 86400000));
  if (iso === tomorrow) return 'Tomorrow';
  return d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
};

const QuickBookModal = ({ open, onClose, onBooked, showToast, pinNotice }) => {
  const [name, setName] = useState('');
  const [matches, setMatches] = useState([]);
  const [matchesOpen, setMatchesOpen] = useState(false);
  const [linkedClient, setLinkedClient] = useState(null);

  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState('');
  const [chosen, setChosen] = useState([]);

  const [pageStart, setPageStart] = useState(() => new Date());
  const [slots, setSlots] = useState([]);
  const [filtered, setFiltered] = useState(0);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [showContact, setShowContact] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [byPhone, setByPhone] = useState(false);

  const [saving, setSaving] = useState(false);
  const [receipts, setReceipts] = useState([]);

  // Import mode: moving an appointment she typed into Google Calendar into this
  // system. Kept in the same modal because the second half of the job -- pick
  // treatments, book -- is identical.
  const [mode, setMode] = useState('new');
  const [imports, setImports] = useState([]);
  const [importsLoading, setImportsLoading] = useState(false);
  const [importsError, setImportsError] = useState('');
  const [importTarget, setImportTarget] = useState(null);

  // The client's last visit, so "same again" is one tap. This is the point of
  // putting bookings in here rather than in a calendar: the calendar remembers
  // when, this remembers what.
  const [lastVisit, setLastVisit] = useState(null);
  const [lastVisitDropped, setLastVisitDropped] = useState([]);
  const [lastVisitLoading, setLastVisitLoading] = useState(false);

  const nameRef = useRef(null);

  // ── Treatments ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open || catalog) return;
    supabaseService
      .getTreatments()
      .then(setCatalog)
      .catch((err) => {
        console.error('Could not load treatments:', err);
        setCatalogError('Could not load your treatments. Close and reopen to try again.');
      });
  }, [open, catalog]);

  useEffect(() => {
    if (open) setTimeout(() => nameRef.current?.focus(), 50);
  }, [open]);

  const duration = chosen.reduce((sum, t) => sum + (Number(t.duration_minutes) || 0), 0);
  // MAX, not sum: two demanding treatments still need one rest at the end, not
  // one each. Same rule as the rebook modal.
  const rest = chosen.reduce((max, t) => Math.max(max, Number(t.rest_minutes) || 0), 0);
  // Consultation-style rows carry no duration and cannot be auto-scheduled.
  const missingDuration = chosen.some((t) => !Number(t.duration_minutes));

  const toggleTreatment = (t) => {
    setSelectedSlot(null);
    setChosen((prev) =>
      prev.some((c) => c.id === t.id) ? prev.filter((c) => c.id !== t.id) : [...prev, t]
    );
  };

  // ── Import from Google ───────────────────────────────────────────────────
  //
  // Her existing entries block their own slots: availability reads every event
  // in her diary as busy, so "Sarah, Tue 2pm" typed into Google means the picker
  // will never offer Tue 2pm. An import therefore cannot go through the slot
  // picker at all — it books the event's OWN time directly. That works because
  // create_booking re-checks availability in Postgres only, with no Google leg,
  // so the source event does not block itself. Everything else in the database
  // still does, so this cannot double-book against a real appointment.

  const loadImports = useCallback(async () => {
    setImportsLoading(true);
    setImportsError('');
    try {
      const today = toISODate(new Date());
      const until = toISODate(new Date(Date.now() + 60 * 86400000));
      const busy = await supabaseService.getBusyPeriods({ startDate: today, endDate: until });
      const nowMs = Date.now();
      setImports(
        (busy || []).filter(
          (b) =>
            b.eventId &&
            // Untagged only. A tagged event is one this system wrote, so it is
            // already a booking — importing it would duplicate the appointment
            // and deleting it would take a real one out of her calendar.
            !b.bookingId &&
            // All-day entries carry no usable time and are usually notes or days
            // off rather than appointments.
            !b.allDay &&
            Date.parse(b.start) > nowMs
        )
      );
    } catch (err) {
      console.error('Could not read the calendar:', err);
      setImportsError(err.message || 'Could not read your Google Calendar.');
      setImports([]);
    } finally {
      setImportsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && mode === 'import' && !imports.length && !importsLoading && !importsError) {
      loadImports();
    }
  }, [open, mode, imports.length, importsLoading, importsError, loadImports]);

  // Slots exist on a 15-minute grid from opening time, so an event at 09:20 is
  // not a bookable time and create_booking would reject it. Rounded to the
  // nearest grid mark, and she is shown that it happened rather than finding out
  // when the client turns up ten minutes out.
  const roundToGrid = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const total = Math.round((h * 60 + m) / 15) * 15;
    const rh = Math.floor(total / 60) % 24;
    return `${String(rh).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const importDate = importTarget ? importTarget.start.slice(0, 10) : null;
  const importRawTime = importTarget ? importTarget.start.slice(11, 16) : null;
  const importTime = importRawTime ? roundToGrid(importRawTime) : null;
  const importShifted = importRawTime && importTime !== importRawTime;

  const pickImport = (ev) => {
    setImportTarget(ev);
    // Her calendar summaries are typically just the person's name, sometimes
    // "Name - treatment". Take the part before the dash and let her correct it.
    const guess = (ev.summary || '').split(/\s+[-–—]\s+/)[0].trim();
    setName(guess);
    setSelectedSlot(null);
    setLinkedClient(null);
  };

  // ── Client autocomplete ──────────────────────────────────────────────────
  //
  // Typing a name searches existing clients. Tapping one books against that
  // identity, which is what keeps duplicate records down without adding a step.
  // Not tapping anything is a brand-new, name-only client.

  useEffect(() => {
    if (!open) return undefined;
    const q = name.trim();
    if (q.length < 2 || linkedClient) {
      setMatches([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      supabaseService
        .searchClients(q)
        .then((rows) => {
          setMatches((rows || []).slice(0, 5));
          setMatchesOpen(true);
        })
        .catch((err) => {
          // Not worth interrupting her for. Without it she simply gets a new
          // client record instead of a linked one.
          console.error('Client search failed:', err);
          setMatches([]);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [name, open, linkedClient]);

  const linkClient = async (row) => {
    setLinkedClient(row);
    setName(row.name || '');
    // A name-keyed client has no real address — see the client key in
    // 20260824020000. `row.email` is an identity token there, not an address,
    // and must never reach the email field or the bookings table.
    setEmail(row.email && !row.email.startsWith('name:') ? row.email : '');
    setPhone(row.phone || '');
    setMatchesOpen(false);
    setLastVisit(null);
    setLastVisitDropped([]);

    // Works for a walk-in she created herself as well as an account holder:
    // get_client_history takes the same key search_clients handed back.
    setLastVisitLoading(true);
    try {
      const history = await supabaseService.getClientHistory(row.email);
      const previous = (history || []).find(
        (v) => v.status === 'approved' && (v.treatments || []).length > 0
      );
      setLastVisit(previous || null);
    } catch (err) {
      // Only costs her the shortcut; the full treatment list is right there.
      console.error('Could not load their last visit:', err);
      setLastVisit(null);
    } finally {
      setLastVisitLoading(false);
    }
  };

  // Past lines are matched back to live treatments by id, then by name, because
  // create_booking books by treatment id and a treatment may since have been
  // renamed or retired. Anything that cannot be matched is named rather than
  // silently dropped -- she needs to know the repeat is not actually identical.
  const useSameAsLastTime = () => {
    if (!lastVisit || !catalog) return;
    const all = catalog.all || [];
    const picked = [];
    const dropped = [];
    (lastVisit.treatments || []).forEach((line) => {
      const match =
        all.find((t) => t.id === line.treatment_id) ||
        all.find((t) => (t.name || '').toLowerCase() === (line.name || '').toLowerCase());
      if (match) {
        if (!picked.some((t) => t.id === match.id)) picked.push(match);
      } else if (line.name) {
        dropped.push(line.name);
      }
    });
    setChosen(picked);
    setLastVisitDropped(dropped);
    setSelectedSlot(null);
  };

  const unlinkClient = () => {
    setLinkedClient(null);
    setEmail('');
    setPhone('');
    setLastVisit(null);
    setLastVisitDropped([]);
  };

  // ── Slots ────────────────────────────────────────────────────────────────

  const loadSlots = useCallback(async () => {
    if (!chosen.length || missingDuration) {
      setSlots([]);
      return;
    }
    setSlotsLoading(true);
    setSlotsError('');
    const end = new Date(pageStart);
    end.setDate(end.getDate() + DAYS_PER_PAGE - 1);
    try {
      // getAvailableSlots directly rather than getAdminSlots, for one reason:
      // getAdminSlots unwraps to a bare array to keep its existing callers
      // working, and this screen wants `filtered` — how many times her own
      // Google Calendar removed. That number matters most here, because she is
      // looking at a gap and wondering why 2pm isn't offered. ignoreNotice is
      // what getAdminSlots would have set, and is why she can book later today.
      const result = await supabaseService.getAvailableSlots({
        startDate: toISODate(pageStart),
        endDate: toISODate(end),
        durationMinutes: duration,
        restMinutes: rest,
        ignoreNotice: true,
      });
      setSlots(result.slots);
      setFiltered(result.filtered);
    } catch (err) {
      console.error('Could not load slots:', err);
      // The availability function fails CLOSED on a calendar outage, so an empty
      // list would read as "fully booked" at exactly the wrong moment — with a
      // client waiting. Said out loud instead.
      setSlotsError(err.message || 'Could not load times. Try again.');
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [chosen.length, missingDuration, pageStart, duration, rest]);

  useEffect(() => {
    if (open) loadSlots();
  }, [open, loadSlots]);

  const slotsByDay = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      if (!map[s.slot_date]) map[s.slot_date] = [];
      map[s.slot_date].push(s);
    });
    return map;
  }, [slots]);

  const days = Object.keys(slotsByDay).sort();
  const atStart = toISODate(pageStart) === toISODate(new Date());

  const shiftPage = (delta) => {
    const next = new Date(pageStart);
    next.setDate(next.getDate() + delta);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setPageStart(next < today ? today : next);
    setSelectedSlot(null);
  };

  // ── Booking ──────────────────────────────────────────────────────────────

  const bookDate = importTarget ? importDate : selectedSlot?.slot_date;
  const bookTime = importTarget ? importTime : selectedSlot?.slot_time;
  const canBook = Boolean(name.trim() && chosen.length && bookDate && bookTime && !saving && !missingDuration);

  const handleBook = async () => {
    if (!canBook) return;
    setSaving(true);
    try {
      const result = await supabaseService.rebookClient({
        name: name.trim(),
        // The whole point: null rather than a placeholder. A fake address would
        // be genuinely posted to Resend, fail, and land in her failure banner.
        email: email.trim() || null,
        phone: phone.trim() || null,
        date: bookDate,
        time: bookTime,
        treatmentIds: chosen.map((t) => t.id),
        notes: notes.trim() || null,
        source: byPhone ? 'admin_manual' : 'admin_rebook',
        treatmentNames: chosen.map((t) => t.name).join(', '),
        totalDuration: duration,
      });

      setReceipts((prev) => [
        {
          id: result.id,
          name: name.trim(),
          when: `${formatDayLabel(bookDate)} ${formatTime(bookTime)}`,
          calendared: !!result.calendarEventId,
        },
        ...prev,
      ]);

      // Only now remove the entry she typed into Google, and only if the
      // replacement actually reached her calendar. The order is the whole point:
      // if the new event failed and we deleted the old one anyway, the
      // appointment would vanish from the one place she looks while the
      // dashboard went on showing it. Better a duplicate she can see than a
      // booking she cannot.
      if (importTarget && result.calendarEventId) {
        try {
          await supabaseService.deleteCalendarEventById(importTarget.eventId);
          setImports((prev) => prev.filter((i) => i.eventId !== importTarget.eventId));
        } catch (err) {
          console.error('Could not remove the original calendar entry:', err);
          pinNotice?.(
            'warning',
            `${name.trim()} is booked, but the original Google Calendar entry is still there. ` +
              'Delete it yourself or that time stays blocked twice.'
          );
        }
      } else if (importTarget) {
        pinNotice?.(
          'warning',
          `${name.trim()} is booked, but it did not reach your Google Calendar, so I have left ` +
            'the original entry alone. Nothing has been lost — check the booking and try again.'
        );
      }

      setImportTarget(null);

      // Four honest outcomes. `emailSkipped` is not a failure — it means there
      // was no address, which is the normal case here — but it must never be
      // reported as "client notified".
      if (importTarget && !result.calendarEventId) {
        // Already reported immediately above; do not say it twice.
      } else if (!result.calendarEventId) {
        pinNotice?.(
          'warning',
          `${name.trim()} is booked, but it did not reach your Google Calendar. Add it by hand.`
        );
      } else if (result.emailSkipped) {
        showToast?.('success', `${name.trim()} booked — in your calendar. No email (no address).`);
      } else if (result.notified) {
        showToast?.('success', `${name.trim()} booked — confirmation emailed.`);
      } else {
        pinNotice?.(
          'warning',
          `${name.trim()} is booked and in your calendar, but the confirmation email may not have arrived.`
        );
      }

      // Stay open for the next one. Treatments and the week she is looking at
      // are kept, because entering ten appointments in a row is the actual use
      // case — name, time, Book, repeat.
      setName('');
      setLinkedClient(null);
      setEmail('');
      setPhone('');
      setNotes('');
      setSelectedSlot(null);
      setMatches([]);
      onBooked?.();
      loadSlots();
      setTimeout(() => nameRef.current?.focus(), 50);
    } catch (err) {
      console.error('Quick book failed:', err);
      if (err.slotTaken) {
        setSelectedSlot(null);
        loadSlots();
      }
      showToast?.('error', err.message || 'Could not book that. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="qb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qb-head">
          <h3>Book someone in</h3>
          <button type="button" className="qb-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="qb-body">
          <div className="qb-tabs">
            <button
              type="button"
              className={mode === 'new' ? 'on' : ''}
              onClick={() => { setMode('new'); setImportTarget(null); }}
            >
              New booking
            </button>
            <button
              type="button"
              className={mode === 'import' ? 'on' : ''}
              onClick={() => setMode('import')}
            >
              From Google Calendar
            </button>
          </div>

          {mode === 'import' && !importTarget && (
            <div className="qb-field">
              <label>Pick an appointment to move across</label>
              {importsLoading && <p className="qb-muted">Reading your calendar…</p>}
              {importsError && (
                <p className="qb-warn">
                  ⚠️ {importsError}{' '}
                  <button type="button" className="qb-inline-btn" onClick={loadImports}>Try again</button>
                </p>
              )}
              {!importsLoading && !importsError && imports.length === 0 && (
                <p className="qb-muted">
                  Nothing left to move across — everything timed in the next 60 days is already
                  in here.
                </p>
              )}
              <ul className="qb-imports">
                {imports.map((ev) => (
                  <li key={ev.eventId}>
                    <button type="button" onClick={() => pickImport(ev)}>
                      <span className="qb-import-when">
                        {formatDayLabel(ev.start.slice(0, 10))} {formatTime(ev.start.slice(11, 16))}
                      </span>
                      <span className="qb-import-what">{ev.summary || '(no title)'}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="qb-muted">
                Only your own entries are listed. Anything already booked through this system is
                left out, and days blocked out are not touched.
              </p>
            </div>
          )}

          {importTarget && (
            <div className="qb-import-target">
              <div>
                <strong>{formatDayLabel(importDate)} at {formatTime(importTime)}</strong>
                <span>{importTarget.summary || '(no title)'}</span>
                {importShifted && (
                  <em>
                    Moved from {formatTime(importRawTime)} — appointments start on the quarter hour.
                  </em>
                )}
              </div>
              <button type="button" className="qb-inline-btn" onClick={() => setImportTarget(null)}>
                Pick another
              </button>
            </div>
          )}

          {/* 1 — who */}
          <div className="qb-field qb-name-field">
            <label htmlFor="qb-name">Name</label>
            <input
              id="qb-name"
              ref={nameRef}
              type="text"
              value={name}
              autoComplete="off"
              placeholder="Who's it for?"
              onChange={(e) => {
                setName(e.target.value);
                if (linkedClient) unlinkClient();
              }}
            />
            {linkedClient && (
              <button type="button" className="qb-linked" onClick={unlinkClient}>
                <Check size={13} />
                {linkedClient.visits > 0
                  ? `${linkedClient.visits} ${linkedClient.visits === 1 ? 'visit' : 'visits'} — tap to unlink`
                  : 'existing client — tap to unlink'}
              </button>
            )}

            {matchesOpen && matches.length > 0 && !linkedClient && (
              <ul className="qb-matches">
                {matches.map((m) => (
                  <li key={m.email}>
                    <button type="button" onClick={() => linkClient(m)}>
                      <Search size={13} />
                      <span className="qb-match-name">{m.name}</span>
                      <span className="qb-match-meta">
                        {m.visits} {m.visits === 1 ? 'visit' : 'visits'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* The shortcut that makes this worth using over her calendar: the
              calendar remembers when they came, this remembers what they had. */}
          {linkedClient && (
            <div className="qb-again">
              {lastVisitLoading && <span className="qb-muted">Looking up their last visit…</span>}

              {!lastVisitLoading && !lastVisit && (
                <span className="qb-muted">No past visit to repeat — pick treatments below.</span>
              )}

              {!lastVisitLoading && lastVisit && (
                <button type="button" className="qb-again-btn" onClick={useSameAsLastTime}>
                  <span className="qb-again-label">Same as last time</span>
                  <span className="qb-again-what">
                    {(lastVisit.treatments || []).map((t) => t.name).filter(Boolean).join(', ')}
                  </span>
                  <span className="qb-again-when">{formatDayLabel(lastVisit.date)}</span>
                </button>
              )}

              {lastVisitDropped.length > 0 && (
                <span className="qb-warn">
                  {lastVisitDropped.join(', ')} {lastVisitDropped.length === 1 ? 'is' : 'are'} no
                  longer on your menu, so {lastVisitDropped.length === 1 ? 'it is' : 'they are'} not
                  included.
                </span>
              )}
            </div>
          )}

          {/* 2 — what */}
          <div className="qb-field">
            <label>Treatments</label>
            {catalogError && <p className="qb-warn">{catalogError}</p>}
            {!catalog && !catalogError && <p className="qb-muted">Loading…</p>}
            {catalog && (
              <div className="qb-chips">
                {catalog.all.map((t) => {
                  const on = chosen.some((c) => c.id === t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`qb-chip${on ? ' on' : ''}`}
                      onClick={() => toggleTreatment(t)}
                    >
                      {t.name}
                      {t.duration_minutes ? <span>{t.duration_minutes}m</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
            {missingDuration && (
              <p className="qb-warn">
                One of those has no set length, so I can't work out a time for it. Book it by phone
                instead.
              </p>
            )}
            {chosen.length > 0 && !missingDuration && (
              <p className="qb-summary">
                {duration} min{rest ? ` + ${rest} min rest` : ''}
              </p>
            )}
          </div>

          {/* 3 — when */}
          {!importTarget && chosen.length > 0 && !missingDuration && (
            <div className="qb-field">
              <div className="qb-slot-nav">
                <button type="button" onClick={() => shiftPage(-DAYS_PER_PAGE)} disabled={atStart || slotsLoading}>
                  <ChevronLeft size={16} />
                </button>
                <span>{atStart ? 'Next 7 days' : `From ${formatDayLabel(toISODate(pageStart))}`}</span>
                <button type="button" onClick={() => shiftPage(DAYS_PER_PAGE)} disabled={slotsLoading}>
                  <ChevronRight size={16} />
                </button>
              </div>

              {slotsLoading && <p className="qb-muted">Finding times…</p>}

              {!slotsLoading && slotsError && <p className="qb-warn">⚠️ {slotsError}</p>}

              {!slotsLoading && !slotsError && days.length === 0 && (
                <p className="qb-muted">Nothing free in these 7 days. Try the arrow.</p>
              )}

              {!slotsLoading && !slotsError && filtered > 0 && (
                <p className="qb-muted">{filtered} more taken up by your own calendar.</p>
              )}

              {!slotsLoading &&
                !slotsError &&
                days.map((day) => (
                  <div key={day} className="qb-day">
                    <span className="qb-day-label">{formatDayLabel(day)}</span>
                    <div className="qb-times">
                      {slotsByDay[day].map((s) => {
                        const on =
                          selectedSlot?.slot_date === s.slot_date &&
                          selectedSlot?.slot_time === s.slot_time;
                        return (
                          <button
                            key={`${s.slot_date}-${s.slot_time}`}
                            type="button"
                            className={`qb-time${on ? ' on' : ''}`}
                            onClick={() => setSelectedSlot(s)}
                          >
                            {formatTime(s.slot_time)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* 4 — everything she usually doesn't need */}
          <button
            type="button"
            className="qb-disclosure"
            onClick={() => setShowContact((v) => !v)}
          >
            {showContact ? '− ' : '+ '}Contact details and notes (optional)
          </button>

          {showContact && (
            <div className="qb-extra">
              <div className="qb-field">
                <label htmlFor="qb-email">Email</label>
                <input
                  id="qb-email"
                  type="email"
                  value={email}
                  placeholder="Leave blank if they haven't given one"
                  onChange={(e) => setEmail(e.target.value)}
                />
                <p className="qb-muted">
                  With an address they get a confirmation and a reminder. Without one, nothing is
                  sent — you tell them the time yourself.
                </p>
              </div>
              <div className="qb-field">
                <label htmlFor="qb-phone">Phone</label>
                <input id="qb-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="qb-field">
                <label htmlFor="qb-notes">Notes</label>
                <textarea id="qb-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <label className="qb-toggle">
                <input type="checkbox" checked={byPhone} onChange={(e) => setByPhone(e.target.checked)} />
                <span>Taken over the phone (not in person)</span>
              </label>
            </div>
          )}

          {receipts.length > 0 && (
            <div className="qb-receipts">
              <span className="qb-receipts-title">Booked just now</span>
              <ul>
                {receipts.map((r) => (
                  <li key={r.id}>
                    <Check size={13} /> {r.name} — {r.when}
                    {!r.calendared && <em> (not in calendar)</em>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="qb-foot">
          <button type="button" className="qb-cancel" onClick={onClose}>
            {receipts.length ? 'Done' : 'Cancel'}
          </button>
          <button type="button" className="qb-book" disabled={!canBook} onClick={handleBook}>
            {saving ? <Loader2 size={16} className="qb-spin" /> : null}
            {bookDate && bookTime
              ? `${importTarget ? 'Move across' : 'Book'} ${formatDayLabel(bookDate)} ${formatTime(bookTime)}`
              : 'Book'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickBookModal;
